import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import {
	useAssignTicketMutation,
	useSnoozeTicketMutation,
	useUnsnoozeTicketMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { TextArea } from '../../../web/components-v2/TextArea';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { SupportTimePicker } from '../../components/supportShared/SupportTimePicker';
import {
	formatSupportTime,
	getSupportTimeError,
} from '../../components/supportShared/SupportTimePicker/supportTimePresets';
import { SnoozeGlyph } from './inboxIcons';
import { styles } from './styles';
import { getSnoozedUntil } from './supportTicketPresentation';

/** Matches the API's limit, so the counter and the server agree on what fits. */
export const SNOOZE_NOTE_LENGTH_MAX = 2000;

interface SnoozeTicketPopoverProps {
	ticket: ISupportTicket;
	/** Called after a snooze succeeds, so the parent can move on to the next ticket. */
	onSnoozed?: () => void;
}

export const SnoozeTicketPopover: React.FC<SnoozeTicketPopoverProps> = ({ ticket, onSnoozed }) => {
	const [open, setOpen] = React.useState(false);
	const [now, setNow] = React.useState(() => new Date());
	const [until, setUntil] = React.useState<Date | null>(null);
	const [note, setNote] = React.useState('');
	const [unassignOnSnooze, setUnassignOnSnooze] = React.useState(false);
	const snooze = useSnoozeTicketMutation(ticket.id);
	const unsnooze = useUnsnoozeTicketMutation(ticket.id);
	const assign = useAssignTicketMutation(ticket.id);
	const unassignCheckboxId = React.useId();

	const snoozedUntil = getSnoozedUntil(ticket);
	const isAssigned = !!ticket.assigneeId;
	const timeError = getSupportTimeError(until, now);
	const triggerLabel = snoozedUntil ? `Snoozed until ${formatSupportTime(snoozedUntil, now)}` : 'Snooze ticket';

	const changeOpen = (next: boolean) => {
		if (next) {
			// The presets and the limits read from one clock, taken when the agent opens the picker.
			setNow(new Date());
			setUntil(null);
			setNote('');
			setUnassignOnSnooze(false);
		}
		setOpen(next);
	};

	// Snooze before unassigning: a failed snooze leaves the assignee alone, and an Active unassigned ticket
	// would be open to round-robin in between. The wake routes an unassigned ticket.
	const submit = async () => {
		if (!until || timeError) return;
		try {
			await snooze.mutateAsync({ until: until.toISOString(), note });
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Failed to snooze ticket.'));
			return;
		}
		const snoozedMessage = `Snoozed #${ticket.number} until ${formatSupportTime(until, new Date())}`;
		if (isAssigned && unassignOnSnooze) {
			try {
				await assign.mutateAsync('');
				toast.success(`${snoozedMessage} and unassigned it`);
			} catch {
				// The API's reason would replace the part the agent needs: the snooze did go through.
				toast.error(`${snoozedMessage}, but couldn't unassign it.`);
			}
		} else {
			toast.success(snoozedMessage);
		}
		setOpen(false);
		onSnoozed?.();
	};

	const wake = () => {
		unsnooze.mutate(undefined, {
			onSuccess: () => setOpen(false),
			onError: error => toast.error(getApiErrorMessage(error, 'Failed to unsnooze ticket.')),
		});
	};

	return (
		<Popover open={open} onOpenChange={changeOpen}>
			<PopoverTrigger asChild>
				<Button
					type='button'
					variant='outline'
					size={snoozedUntil ? 'default' : 'icon'}
					title={triggerLabel}
					aria-label={triggerLabel}
					styles={snoozedUntil ? [styles.actionPrimaryBtn, styles.actionIconBtnActive] : [styles.actionIconBtn]}
				>
					<SnoozeGlyph size={18} />
					{snoozedUntil && <span>{formatSupportTime(snoozedUntil, now)}</span>}
				</Button>
			</PopoverTrigger>
			<PopoverContent align='end' styles={[styleSheet.content]}>
				<div className={css(styleSheet.header)}>{snoozedUntil ? 'Change snooze' : 'Snooze until'}</div>
				{snoozedUntil && (
					<div className={css(styleSheet.current)}>
						<span>Wakes {formatSupportTime(snoozedUntil, now)}</span>
						<Button type='button' variant='ghost' size='sm' onClick={wake} isLoading={unsnooze.isPending}>
							Unsnooze
						</Button>
					</div>
				)}
				<SupportTimePicker value={until} onChange={setUntil} now={now} />
				<TextArea
					aria-label='Reminder note'
					placeholder='Reminder for when it wakes (optional). Saved as an internal note.'
					value={note}
					maxLength={SNOOZE_NOTE_LENGTH_MAX}
					rows={3}
					onChange={event => setNote(event.target.value)}
				/>
				{isAssigned && (
					<div className={css(styleSheet.unassign)}>
						<Checkbox
							id={unassignCheckboxId}
							checked={unassignOnSnooze}
							onCheckedChange={checked => setUnassignOnSnooze(checked === true)}
						/>
						<label htmlFor={unassignCheckboxId}>Snooze and unassign</label>
					</div>
				)}
				{until && timeError && (
					<div className={css(styleSheet.error)} role='alert'>
						{timeError}
					</div>
				)}
				<div className={css(styleSheet.footer)}>
					<Button type='button' variant='ghost' size='sm' onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<Button
						type='button'
						variant='primary'
						size='sm'
						onClick={() => void submit()}
						disabled={!!timeError}
						isLoading={snooze.isPending || assign.isPending}
					>
						Snooze
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
};

const styleSheet = StyleSheet.create({
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.space[12],
		width: 320,
		maxWidth: 'calc(100vw - 32px)',
		maxHeight: 'calc(100vh - 96px)',
		overflowY: 'auto',
	},
	header: {
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.sm,
	},
	current: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.space[8],
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	unassign: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.space[8],
		fontSize: THEME.typography.fontSizes.sm,
		cursor: 'pointer',
	},
	error: {
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.xs,
	},
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: THEME.space[8],
	},
});
